import * as React from "react";
import {Link, LinkProps} from "react-router-dom";
// @ts-ignore
import clsx from "clsx";

type Variant = "primary" | "secondary" | "tertiary" | "ghost" | "navy" | "danger" | "warning" | "outline";
type Size = "sm" | "md" | "lg";

type CommonProps = {
    label?: string; // optional if you pass children
    children?: React.ReactNode;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    variant?: Variant;
    size?: Size;
    fullWidth?: boolean;
    loading?: boolean;
    disabled?: boolean;
    className?: string;
    title?: string;          // optional tooltip
    onClick?: (e: React.MouseEvent) => void;
    type?: "button" | "submit" | "reset";
    target?: React.HTMLAttributeAnchorTarget; // e.g. "_blank"
    rel?: string;
};

type ButtonAsButton = CommonProps & {
    to?: undefined;
    href?: undefined;
};

type ButtonAsInternalLink = CommonProps & {
    to: LinkProps["to"];     // internal link via react-router
    href?: undefined;
};

type ButtonAsExternalLink = CommonProps & {
    href: string;            // external link
    to?: undefined;
};

type Props = ButtonAsButton | ButtonAsInternalLink | ButtonAsExternalLink;


const VARIANT_CLASSES: Record<Variant, string> = {
    primary: "bg-orange-600 text-white hover:bg-orange-500 focus-visible:outline-orange-500",
    secondary: "bg-blue-900 text-white hover:bg-blue-800 focus-visible:outline-blue-800",
    tertiary: "bg-gray-200 text-gray-900 hover:bg-gray-300 focus-visible:outline-gray-400",
    ghost: "bg-transparent text-gray-900 hover:bg-gray-100 focus-visible:outline-gray-300",
    navy: "bg-blue-800 text-white hover:bg-blue-700 focus-visible:outline-blue-700",
    danger: "bg-red-600 text-white hover:bg-red-500 focus-visible:outline-red-500",
    warning: "bg-yellow-500 text-white hover:bg-yellow-400 focus-visible:outline-yellow-400",
    outline: "bg-white text-gray-900 hover:bg-gray-100 focus-visible:outline-gray-300 border border-gray-300",
};

const SIZE_CLASSES: Record<Size, string> = {
    sm: "px-3 py-1.5 text-sm rounded-md",
    md: "px-4 py-2 text-sm rounded-lg",
    lg: "px-5 py-2.5 text-base rounded-2xl",
};

function baseClasses({
                         variant,
                         size,
                         fullWidth,
                     }: {
    variant: Variant;
    size: Size;
    fullWidth?: boolean;
}) {
    return clsx(
        "inline-flex items-center justify-center font-semibold shadow-xs transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60 disabled:cursor-not-allowed",
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        fullWidth && "w-full"
    );
}

function Spinner() {
    return (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25"/>
            <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="4" fill="none"/>
        </svg>
    );
}

export default function Button(props: Props) {
    const {
        label,
        children,
        leftIcon,
        rightIcon,
        variant = "primary",
        size = "lg",
        fullWidth,
        loading,
        disabled,
        className,
        title,
        onClick,
        type = "button",
        target,
        rel,
    } = props;

    const content = (
        <span className="inline-flex items-center gap-2">
      {loading ? <Spinner/> : leftIcon}
            {children ?? label}
            {rightIcon}
    </span>
    );

    const classes = clsx(baseClasses({variant, size, fullWidth}), className);
    const isDisabled = !!disabled || !!loading;

    // External link
    if ("href" in props && props.href) {
        const finalRel = target === "_blank" ? rel ?? "noopener noreferrer" : rel;

        // For accessibility: simulate disabled anchor if loading/disabled
        const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
            if (isDisabled) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            onClick?.(e);
        };

        return (
            <a
                href={props.href}
                target={target}
                rel={finalRel}
                title={title}
                className={classes}
                onClick={handleClick}
                aria-disabled={isDisabled || undefined}
                aria-busy={loading || undefined}
                tabIndex={isDisabled ? -1 : 0}
            >
                {content}
            </a>
        );
    }

    // Internal link
    if ("to" in props && props.to !== undefined) {
        const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
            if (isDisabled) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            onClick?.(e);
        };

        return (
            <Link
                to={props.to}
                title={title}
                className={classes}
                onClick={handleClick}
                aria-disabled={isDisabled || undefined}
                aria-busy={loading || undefined}
                tabIndex={isDisabled ? -1 : 0}
            >
                {content}
            </Link>
        );
    }

    // Plain button
    return (
        <button
            type={type}
            title={title}
            className={classes}
            onClick={onClick}
            disabled={isDisabled}
            aria-busy={loading || undefined}
        >
            {content}
        </button>
    );
}
